import type { EngineRegime } from '../../types';
import type { MessageCallbacks } from './types';

export function handleSignals(obj: any, cb: MessageCallbacks): boolean {
  if (!((obj?.kind === 'SIGNALS' || obj?.type === 'signals') && (obj.data?.signals || obj.signals))) return false;

  const data = obj.data || obj;
  const secId = ((data.sec_id || data.secId || obj.secId) || '').toUpperCase();
  const signals = data.signals || obj.signals;
  if (!secId) return true;

  const regimeStr = (signals.regime || 'neutral').toLowerCase();
  const confidence = Number(signals.regime_confidence ?? 0);
  const iso = (typeof data.timestamp_iso === 'string') ? data.timestamp_iso : new Date().toISOString();

  const engineRegime: EngineRegime =
    regimeStr === 'accumulation' ? 'accumulation' :
    regimeStr === 'distribution' ? 'distribution' :
    regimeStr === 'volatile' || regimeStr === 'VOLATILE' ? 'VOLATILE' :
    regimeStr === 'trending' || regimeStr === 'active' || regimeStr === 'ACTIVE' ? 'ACTIVE' :
    regimeStr === 'sideways' || regimeStr === 'quiet' || regimeStr === 'QUIET' ? 'QUIET' :
    regimeStr === 'dormant' || regimeStr === 'DORMANT' ? 'DORMANT' :
    'neutral';

  cb.setRegimeBySymbol(prev => {
    const next = new Map(prev);
    const current = next.get(secId);
    next.set(secId, { ticker: secId, regime: engineRegime, confidence, last_changed: iso, prev_regime: current?.regime });
    return next;
  });

  if (secId === cb.selectedSymbol) {
    const ofiVal = Number(signals.ofi ?? 0);
    const trueOfi = Number(signals.true_ofi ?? 0);
    const l2Imbalance = Number(signals.l2_imbalance ?? signals.l2_ofi ?? 0);
    const vpin = Number(signals.vpin ?? 0);
    const divergence = Number(signals.divergence ?? 0);
    const ofiMomentum = Number(signals.ofi_momentum ?? 0);

    cb.setAnalytics(prev => {
      const base = prev ?? cb.getMinimalAnalytics();
      const ofiAbs = Math.abs(ofiVal);
      const buyFlow = ofiVal > 0 ? ofiAbs : 0;
      const sellFlow = ofiVal < 0 ? ofiAbs : 0;
      const depthImb = l2Imbalance !== 0 ? l2Imbalance : (base.depthImbalance?.imbalance ?? 0);
      const signalBidVolume = Number(signals.bid_volume ?? signals.bidVolume ?? 0);
      const signalAskVolume = Number(signals.ask_volume ?? signals.askVolume ?? 0);
      const hasSignalDepth = signalBidVolume > 0 || signalAskVolume > 0;
      const hasBaseDepth = (base.depthImbalance?.bidVolume ?? 0) > 0 || (base.depthImbalance?.askVolume ?? 0) > 0;
      const bidVol = hasSignalDepth ? signalBidVolume : hasBaseDepth ? (base.depthImbalance?.bidVolume ?? 0) : (depthImb > 0 ? 50 + depthImb * 50 : 50);
      const askVol = hasSignalDepth ? signalAskVolume : hasBaseDepth ? (base.depthImbalance?.askVolume ?? 0) : (100 - (depthImb > 0 ? 50 + depthImb * 50 : 50));

      return {
        ...base,
        orderFlowImbalance: {
          buyFlow: buyFlow || base.orderFlowImbalance?.buyFlow || 0,
          sellFlow: sellFlow || base.orderFlowImbalance?.sellFlow || 0,
          netFlow: trueOfi || ofiVal || base.orderFlowImbalance?.netFlow || 0,
          flowRatio: ofiVal || base.orderFlowImbalance?.flowRatio || 0,
          timeWindow: base.orderFlowImbalance?.timeWindow || 60,
        },
        depthImbalance: l2Imbalance !== 0 ? {
          topN: base.depthImbalance?.topN ?? 5,
          bidVolume: bidVol,
          askVolume: askVol,
          imbalance: depthImb,
          imbalancePercent: depthImb * 100,
        } : base.depthImbalance,
        ml: {
          ...base.ml,
          enabled: true,
          regime: {
            regime: regimeStr as any,
            confidence,
            regime_features: {
              spread_asymmetry: Number(signals.regime_spread_asymmetry ?? 0),
              aggression_factor: Number(signals.regime_aggression ?? 1),
              optimal_spread_multiplier: 1,
            },
            model_version: '',
            timestamp: iso,
          },
        },
        dseSignals: {
          ofi: ofiVal,
          ofi_momentum: ofiMomentum,
          true_ofi: trueOfi,
          vpin,
          divergence,
          divergence_zscore: Number(signals.divergence_zscore ?? 0),
          regime: regimeStr,
          regime_confidence: confidence,
        },
      } as any;
    });

    const ticker = cb.labelForSecId(secId) || secId;
    if (vpin > 0.6) {
      cb.pushActivity({
        id: `F1K_SPIKE_${secId}_${Date.now()}_${cb.nextActivitySeq()}`,
        kind: 'F1K_SPIKE',
        ticker,
        title: `${ticker}: High VPIN ${(vpin * 100).toFixed(0)}% — adverse selection risk`,
        timestamp: iso,
        severity: 'warning',
      });
    }

    const nowMs = Date.now();
    const prevOfiActivity = cb.lastOfiActivityBySecIdRef.current.get(secId);
    const prevVal = prevOfiActivity?.value ?? 0;
    const prevTs = prevOfiActivity?.ts ?? 0;
    const absOfi = Math.abs(ofiVal);
    const prevAbsOfi = Math.abs(prevVal);
    const crossedThreshold = absOfi > 0.3 && prevAbsOfi <= 0.3;
    const directionChanged = Math.sign(ofiVal) !== 0 && Math.sign(ofiVal) !== Math.sign(prevVal);
    const magnitudeShifted = Math.abs(ofiVal - prevVal) >= 0.12;
    const cooldownElapsed = (nowMs - prevTs) >= 12_000;

    if (absOfi > 0.3 && (crossedThreshold || directionChanged || (magnitudeShifted && cooldownElapsed))) {
      cb.pushActivity({
        id: `REGIME_SHIFT_ofi_${secId}_${nowMs}_${cb.nextActivitySeq()}`,
        kind: 'REGIME_SHIFT',
        ticker,
        title: `${ticker}: Strong OFI ${ofiVal > 0 ? 'buy' : 'sell'} pressure (${(ofiVal * 100).toFixed(0)}%)`,
        timestamp: iso,
        severity: 'info',
      });
      cb.lastOfiActivityBySecIdRef.current.set(secId, { value: ofiVal, ts: nowMs });
    }
  }
  return true;
}
